/**
 * JobsDock — global floating dock (bottom-right) showing all in-progress and
 * recently-completed long-running jobs of the current user.
 *
 * - Polls /api/bg-jobs/active every 3s
 * - Each card represents one job (photo_gen | adaptation)
 * - Click on card → reopens the source modal (via window-level event)
 * - Toast on completion
 * - Card disappears 30s after completion (handled by backend cutoff)
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ImageIcon, Wand2, X, CheckCircle2, AlertCircle, Minimize2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  photo_gen:  { Icon: ImageIcon, color: 'bg-violet-500',  label: 'Geração de fotos' },
  adaptation: { Icon: Wand2,     color: 'bg-emerald-500', label: 'Adaptação de anúncio' },
};

const STATUS_LABEL = {
  QUEUED:    'Na fila',
  RUNNING:   'Em andamento',
  SUCCESS:   'Concluído',
  FAILED:    'Falhou',
  PARTIAL:   'Parcial',
  CANCELLED: 'Cancelado',
};

const JobsDock = () => {
  const [jobs, setJobs] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  );
  const completedSeenRef = useRef(new Set()); // to avoid duplicate toasts
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // ── Request browser notification permission once after first activity ─────
  const ensureNotifPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      try {
        const p = await Notification.requestPermission();
        setNotifPermission(p);
      } catch { /* ignore */ }
    }
  };

  const fireOSNotification = (title, body) => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      // Only ping the OS when the tab is hidden — toast already covers focused tabs.
      if (!document.hidden) return;
      // Some browsers throttle if site has many. tag dedupes.
      const n = new Notification(title, {
        body,
        tag: 'magic-place-bg-job',
        icon: '/favicon.ico',
        silent: false,
      });
      n.onclick = () => { try { window.focus(); n.close(); } catch { /* ignore */ } };
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!token) return undefined; // do nothing while logged out
    let cancelled = false;
    let timeoutId = null;
    // Adaptive polling: aggressive (3s) only while there are active jobs;
    // back off to 15s when idle. This keeps the dock responsive during work
    // but eliminates wasted requests when nothing is running (most of the time).
    let hadActiveLastTick = false;

    const fetchActive = async () => {
      try {
        const { data } = await axios.get(`${API}/bg-jobs/active`);
        if (cancelled) return;
        const items = data.items || [];

        // Detect newly-completed jobs (since last fetch) → toast + OS notification once
        items.forEach((j) => {
          if (['SUCCESS', 'FAILED', 'PARTIAL'].includes(j.status) &&
              !completedSeenRef.current.has(j.id)) {
            completedSeenRef.current.add(j.id);
            const kindLabel = KIND_META[j.kind]?.label || 'Tarefa';
            if (j.status === 'SUCCESS') {
              toast.success(`✓ ${kindLabel} concluída`, {
                description: j.label || j.progress_step,
                duration: 6000,
              });
              fireOSNotification(`✓ ${kindLabel} concluída`, j.label || '');
            } else if (j.status === 'FAILED') {
              toast.error(`✗ ${kindLabel} falhou`, {
                description: j.error_message || 'Tente novamente.',
                duration: 8000,
              });
              fireOSNotification(`✗ ${kindLabel} falhou`, j.error_message || '');
            }
          }
        });

        // Ask for OS-notification permission the first time an active job appears
        const hasActive = items.some(j => ['QUEUED', 'RUNNING'].includes(j.status));
        if (hasActive) ensureNotifPermission();

        setJobs(items);
        hadActiveLastTick = hasActive;
      } catch {
        // Silent fail — likely auth missing or backend transient
      } finally {
        if (!cancelled) {
          const nextDelay = hadActiveLastTick ? 3000 : 15000;
          timeoutId = setTimeout(fetchActive, nextDelay);
        }
      }
    };

    fetchActive();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [token]);

  // Don't render anything when not authenticated.
  if (!token) return null;

  const handleReopen = (job) => {
    // Dispatch a global event that pages can listen to in order to reopen
    // their corresponding modal (pages decide what to do based on job.kind/payload).
    window.dispatchEvent(new CustomEvent('bg-job:reopen', { detail: job }));
  };

  const handleDismiss = async (job, e) => {
    e.stopPropagation();
    if (['QUEUED', 'RUNNING'].includes(job.status)) return;
    try {
      await axios.delete(`${API}/bg-jobs/${job.id}`);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
    } catch (err) { /* no-op */ }
  };

  const handleCancel = async (job, e) => {
    e.stopPropagation();
    if (!['QUEUED', 'RUNNING'].includes(job.status)) return;
    if (!window.confirm(`Cancelar "${job.label}"?\n\nO processo em andamento será interrompido.`)) return;
    try {
      await axios.post(`${API}/bg-jobs/${job.id}/cancel`);
      toast.info('Tarefa cancelada', { description: job.label, duration: 3000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível cancelar');
    }
  };

  if (!jobs.length) return null;

  return (
    <div
      data-testid="jobs-dock"
      className="fixed bottom-4 right-4 z-[1000] w-80 max-w-[calc(100vw-32px)] flex flex-col gap-2"
    >
      <div className="flex items-center justify-between bg-slate-900 text-white rounded-t-lg px-3 py-2 shadow-lg">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <Loader2 className={`w-3 h-3 ${jobs.some(j => ['QUEUED','RUNNING'].includes(j.status)) ? 'animate-spin' : ''}`} />
          {jobs.filter(j => ['QUEUED','RUNNING'].includes(j.status)).length} em andamento
          <span className="text-slate-400 font-normal">· {jobs.length} no painel</span>
        </div>
        <div className="flex items-center gap-1">
          {notifPermission === 'default' && (
            <button
              onClick={ensureNotifPermission}
              className="text-[10px] text-slate-300 hover:text-white px-1.5 py-0.5 rounded bg-slate-700 hover:bg-slate-600"
              data-testid="jobs-dock-notif-enable"
              title="Receber notificação do navegador quando concluir"
            >
              🔔 Ativar
            </button>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="text-slate-300 hover:text-white p-1 rounded"
            data-testid="jobs-dock-collapse-btn"
          >
            <Minimize2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
          {jobs.map((job) => {
            const meta = KIND_META[job.kind] || KIND_META.photo_gen;
            const Icon = meta.Icon;
            const isActive = ['QUEUED', 'RUNNING'].includes(job.status);
            const isOk     = job.status === 'SUCCESS';
            const isErr    = job.status === 'FAILED';
            return (
              <div
                key={job.id}
                onClick={() => handleReopen(job)}
                data-testid={`jobs-dock-card-${job.id}`}
                className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 cursor-pointer hover:border-slate-400 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <div className={`${meta.color} w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate" title={job.label}>
                      {job.label}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                      {isActive && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                      {isOk && <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />}
                      {isErr && <AlertCircle className="w-2.5 h-2.5 text-red-600" />}
                      {job.progress_step || STATUS_LABEL[job.status] || job.status}
                    </p>
                    {isActive && (
                      <div className="mt-1.5 w-full bg-slate-100 rounded-full h-1 overflow-hidden">
                        <div
                          className={`${meta.color} h-1 transition-all`}
                          style={{ width: `${Math.max(5, job.progress_pct || 0)}%` }}
                        />
                      </div>
                    )}
                  </div>
                  {isActive ? (
                    <button
                      onClick={(e) => handleCancel(job, e)}
                      className="text-slate-400 hover:text-red-600 p-0.5"
                      data-testid={`jobs-dock-cancel-${job.id}`}
                      title="Cancelar tarefa"
                      aria-label="Cancelar"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => handleDismiss(job, e)}
                      className="text-slate-400 hover:text-slate-700 p-0.5"
                      data-testid={`jobs-dock-dismiss-${job.id}`}
                      aria-label="Dispensar"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JobsDock;
